import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Input, SelectField, TextArea } from '../containers/Form';
import { closeModal } from '../actions/modal';
import { notificationActions } from '../components/Notification/redux/actions';
import Strings from '../Strings';
import Wizard from '../containers/Modal/Wizard';
import actions from '../pages/SuperUser/SuperuserHealthCenter/redux/actions';
import {
  ISSUE_CREATE_SEVERITY_OPTIONS,
  getAssignableUserLabel,
} from '../pages/SuperUser/SuperuserHealthCenter/shared';

export const CREATE_ISSUE_MODAL_NAME = 'create-issue-modal';

function CreateIssueModal(props) {
  const { addIssue, data, onCreateSuccess, showNotification } = props;
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [deviceId, setDeviceId] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [severity, setSeverity] = React.useState('low');
  const [assignedTo, setAssignedTo] = React.useState('');
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightInvalidFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const assignableUsers = React.useMemo(
    () => (Array.isArray(data?.assignableUsers) ? data.assignableUsers : []),
    [data],
  );

  const assigneeOptions = React.useMemo(
    () => [{ value: '', label: Strings.none }].concat(
      assignableUsers.map(user => ({
        value: user.unguessable_id,
        label: getAssignableUserLabel(user),
      })),
    ),
    [assignableUsers],
  );

  const severityOptions = React.useMemo(
    () => ISSUE_CREATE_SEVERITY_OPTIONS.map(option => ({ value: option.value, label: option.label })),
    [],
  );

  React.useEffect(() => {
    const errors = [];

    if (!title.trim()) {
      errors.push({ property: 'title', propertyName: Strings.title, missing: true });
    }

    if (!category.trim()) {
      errors.push({ property: 'category', propertyName: Strings.category, missing: true });
    }

    setValidationErrors(errors);
  }, [category, title]);

  const onSubmit = () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    const payload = {
      title: title.trim(),
      category: category.trim(),
      severity,
    };

    if (description.trim()) {
      payload.description = description.trim();
    }

    if (deviceId.trim()) {
      payload.device_id = deviceId.trim();
    }

    if (assignedTo) {
      payload.assigned_to = assignedTo;
    }

    addIssue(payload).then(resp => {
      if (resp?.type?.endsWith('/error')) {
        showNotification(resp?.response?.data?.error?.message || Strings.errors?.unknownError, 5000, true);
        setIsSaving(false);
        return;
      }

      showNotification(Strings.formatString(Strings.itemSaved, Strings.superuserHealthIssue));
      onCreateSuccess(resp?.response || null);
    });
  };

  const pages = [
    {
      id: 'create-issue-page',
      title: Strings.superuserHealthCreateIssueTitle,
      content: (
        <>
          <Input
            name="issueTitle"
            id="issueTitle"
            label={Strings.title}
            type="text"
            value={title}
            onChange={event => setTitle(event.target.value)}
            isRequired
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={validationErrors.find(v => v.property === 'title')?.errors || []}
          />
          <TextArea
            name="issueDescription"
            id="issueDescription"
            label={Strings.description}
            value={description}
            onChange={event => setDescription(event.target.value)}
            rows={5}
          />
          <Input
            name="issueDeviceId"
            id="issueDeviceId"
            label={Strings.deviceId}
            type="text"
            value={deviceId}
            onChange={event => setDeviceId(event.target.value)}
          />
          <Input
            name="issueCategory"
            id="issueCategory"
            label={Strings.category}
            type="text"
            value={category}
            onChange={event => setCategory(event.target.value)}
            isRequired
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={validationErrors.find(v => v.property === 'category')?.errors || []}
          />
          <SelectField
            name="issueSeverity"
            id="issueSeverity"
            label={Strings.severity}
            value={severity}
            onChange={option => setSeverity(option?.value || 'low')}
            data={severityOptions}
            isSearchable={false}
          />
          <SelectField
            name="issueAssignedTo"
            id="issueAssignedTo"
            label={Strings.assignedTo}
            value={assignedTo}
            onChange={option => setAssignedTo(option?.value || '')}
            data={assigneeOptions}
            isSearchable
            searchByLabelOnly
          />
        </>
      ),
      nextButton: { text: Strings.create },
      emptyFieldsCount: validationErrors.filter(error => error.missing).length,
      canGoNext: !isSaving && validationErrors.length === 0,
    },
  ];

  return (
    <Wizard
      name={CREATE_ISSUE_MODAL_NAME}
      pages={pages}
      onSubmit={onSubmit}
      onNextButtonHover={setHighlightInvalidFields}
      showPagesFilter={false}
    />
  );
}

CreateIssueModal.propTypes = {
  addIssue: PropTypes.func,
  data: PropTypes.shape({
    assignableUsers: PropTypes.array,
    onSuccess: PropTypes.func,
  }),
  onCreateSuccess: PropTypes.func,
  showNotification: PropTypes.func,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  addIssue: payload => dispatch(actions.addIssue(payload)),
  onCreateSuccess: issue => {
    dispatch(closeModal(CREATE_ISSUE_MODAL_NAME));
    if (ownProps.data?.onSuccess) {
      ownProps.data.onSuccess(issue);
    }
  },
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
});

export default connect(null, mapDispatchToProps)(CreateIssueModal);
